<template>
  <div class="import-targets">
    <v-card-text class="title px-lg-16 pt-6 pb-3 px-0">
      {{ $t('import.targets.title') }}
    </v-card-text>

    <v-card-text class="px-lg-16 p-0">
      <v-divider />

      <div class="d-flex flex-lg-row flex-column no-gutters pb-lg-0 pb-6">
        <v-col cols="12" lg="8">
          <div class="subtitle d-flex flex-row align-lg-center align-start">
            <i class="fa-light fa-upload"></i>
            {{ $t('import.targets.label') }}
          </div>
          <div class="description pb-lg-3 pb-6">{{ $t('import.targets.sub-label') }}</div>
        </v-col>

        <v-col cols="12" lg="4">
          <div class="d-flex justify-lg-end justify-center align-center full-height">
            <CustomInputFile
              :key="`import_targets_${resourceKey}`"
              :loading="loading"
              @importAction="importFile"
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              data-cy="targets-input"
              type="s3" />
          </div>
        </v-col>
      </div>

      <v-divider />
    </v-card-text>

    <MapitDialog v-if="dialog" :dialog="dialog.open" @close="dialog.close()">
      <template v-slot:header-icon>
        <v-icon class="text-orange" v-if="dialog.error">fa fa-exclamation-triangle</v-icon>
        <v-icon class="text-gray" v-else-if="dialog.info">fa-solid fa-circle-info</v-icon>
        <v-icon class="text-secondary" v-else>fa fa-check</v-icon>
      </template>

      <template v-slot:header-text>
        <div class="text-center">{{ dialog.title }}</div>
      </template>

      <template v-slot:body>
        <div v-html="dialog.body" class="text-center"></div>
      </template>

      <template v-slot:footer-center>
        <v-btn class="btn-gray" v-if="dialog.showCancel" @click="dialog.close()">{{ $t('buttons.cancel') }} </v-btn>

        <v-btn class="btn-primary" @click="dialog.continue()">{{ $t('buttons.next') }} </v-btn>
      </template>
    </MapitDialog>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onUnmounted, ref, toRefs} from '@vue/composition-api'

import {s3, streamToString} from '@/api/lib/s3-client'
import MapitDialog from '@/components/dialog/MapitDialog.vue'
import CustomInputFile from '@/components/import/CustomInputFile.vue'
import {parseImportResponse} from '@/helpers/import'
import {mapGetters} from '@/utils/map-store'

export default defineComponent({
  name: 'ImportTargets',
  components: {CustomInputFile, MapitDialog},
  props: {
    selectedBranch: {
      type: String,
      required: true,
    },
  },
  setup(props, {root}) {
    const {getUser} = mapGetters(root.$store.getters)
    const {selectedBranch} = toRefs(props)

    const loading = ref<boolean>(false)
    const interval = ref<any>(null)
    const retries = ref<number>(1)

    const resourceKey = ref<number>(Date.now())
    const resource = computed(() => {
      return `kpi-targets/${selectedBranch.value}/${resourceKey.value}`
    })

    const dialog = ref<
      Partial<{
        open: boolean
        title: string
        body: string
        error: boolean
        info: boolean
        showCancel: boolean
        click: () => void
        continue: () => void
        close: () => void
      }>
    >({
      open: false,
      error: false,
      info: false,
      showCancel: false,
      title: '',
      body: '',
      click: () => closeDialog(),
      continue: () => ({}),
      close: () => closeDialog(),
    })

    function renderDialog({
      message,
      messageData,
      error,
      next = () => ({}),
      cancel = false,
    }: {
      message: string
      messageData?: any
      next?: any
      error?: boolean
      cancel?: boolean
    }) {
      loading.value = false

      dialog.value = {
        open: true,
        error,
        showCancel: cancel,
        title: root.$t('import.import-title') as string,
        body: root.$t(message, messageData) as string,
        continue: () => {
          loading.value = false
          closeDialog()
          next()
        },
        close: () => {
          loading.value = false
          closeDialog()
        },
      }
    }

    function closeDialog() {
      dialog.value.open = false
      resourceKey.value = Date.now()
    }

    async function handleUpload(data: any) {
      loading.value = true
      const response = await uploadFileToS3(data)

      if (response.status > 300 || response.error) {
        const responseParsed = parseImportResponse(response)
        renderDialog(responseParsed)

        loading.value = false
        return
      }

      interval.value = setInterval(handleCheckStatus, 5000)
    }

    async function uploadFileToS3(data: any) {
      try {
        // Convert File to Uint8Array for AWS SDK v3 compatibility
        const arrayBuffer = await data.arrayBuffer()
        const uint8Array = new Uint8Array(arrayBuffer)

        const client = await s3()
        await client.upload({
          bucket: process.env.VUE_APP_S3_OFFER_DATA_BUCKET!,
          key: `${resource.value}.xlsx`,
          body: uint8Array,
        })

        return {status: 200}
      } catch (err: any) {
        console.error(err)
        return {status: err.statusCode, error: err.message}
      }
    }

    async function handleCheckStatus() {
      loading.value = true
      const response = await checkUploadStatus()

      if (response.status > 300 && retries.value < 3) {
        retries.value++
        return
      }

      const responseParsed = parseImportResponse(response)
      renderDialog(responseParsed)
      loading.value = false

      clearInterval(interval.value)
      interval.value = null
    }

    async function checkUploadStatus() {
      try {
        const client = await s3()
        const result = await client.getObject({
          Bucket: process.env.VUE_APP_S3_OFFER_DATA_BUCKET!,
          Key: `${resource.value}.RESULT.json`,
        })

        if (!result.Body) throw new Error('the resource does not exist')

        const bodyString = await streamToString(result.Body)
        return {
          status: 200,
          ...JSON.parse(bodyString),
        }
      } catch (err: any) {
        console.error(err)
        return {status: err.statusCode, error: err.message}
      }
    }

    async function importFile(data: any) {
      renderDialog({
        cancel: true,
        message: 'import.targets.confirmation',
        next: () => handleUpload(data),
      })
    }

    onUnmounted(() => {
      clearInterval(interval.value)
      interval.value = null
    })

    return {
      dialog: dialog as any,
      importFile,
      loading,
      resourceKey,
    }
  },
})
</script>
